import { api } from '../lib/api'
import type { AccountMe } from '../types/accountMe'

export type MeProfile = {
  public_id?: string | null
  display_name?: string
  handle?: string
  avatar_url?: string
  cover_url?: string
  motto?: string
  about_short?: string
  lives_in_label?: string
  from_location_label?: string
  links?: Array<{ label: string; url: string }>
  profile_visibility_status?: string
  profile_visibility_message?: string
  user_safe_message?: string | null
  last_moderation_event_id?: string | null
  last_moderation_decision_action?: string | null
  profile_state?: 'draft' | 'published' | 'needs_edits'
  moderation_action?: string
  moderation_category?: string | null
  moderation_reasons?: string[]
  suggested_fixes?: string[]
}

export type Me = AccountMe & {
  ss58_address?: string
  phone_verified?: boolean
  wallet_linked?: boolean
  stripe_connect_ready?: boolean
  seller_eligibility?: {
    can_list: boolean
    can_publish: boolean
    can_withdraw: boolean
    requirements: {
      email_verified: boolean
      phone_verified: boolean
      wallet_linked: boolean
      stripe_connect_ready: boolean
    }
    blockers: Array<{ code: string; message: string }>
  }
  profile: MeProfile
}

export async function getMe() {
  const { data } = await api.get<Me>('/api/me/')
  return data
}

export async function updateMe(payload: { username?: string; handle?: string; email?: string }) {
  const { data } = await api.patch<Me>('/api/me/', payload)
  return data
}

export type MeProfileUpdateResponse = {
  profile: MeProfile
  profile_state?: 'draft' | 'published' | 'needs_edits'
  moderation_action?: string
  moderation_category?: string | null
  moderation_reasons?: string[]
  suggested_fixes?: string[]
} & Partial<MeProfile>

export async function updateMeProfile(payload: Partial<MeProfile>) {
  const { data } = await api.patch<any>('/api/me/profile/', payload)
  if (data && data.profile) return data as MeProfileUpdateResponse
  return {
    ...(data || {}),
    profile: (data || {}) as MeProfile,
  } as MeProfileUpdateResponse
}

export async function updateMeBio(payload: { about_long_md?: string; about_short?: string }) {
  const { data } = await api.patch('/api/me/profile/bio/', payload)
  return data
}

export type ProfileStory = {
  id?: number
  title?: string | null
  subtitle?: string | null
  cover_url?: string | null
  cover_landscape_url?: string | null
  cover_landscape_generated?: boolean
  cover_landscape_generated_at?: string | null
  body_json?: any
  body_html?: string
  body_plain?: string
  schema_version?: number
  word_count?: number
  reading_time_seconds?: number
  created_at?: string
  updated_at?: string
}

export async function getMyProfileStory() {
  const { data } = await api.get<ProfileStory>('/api/me/profile/story/')
  return data
}

export async function updateMyProfileStory(payload: Partial<ProfileStory> & { body_json?: any; body_plain?: string; schema_version?: number }) {
  const { data } = await api.patch<ProfileStory>('/api/me/profile/story/', payload)
  return data
}

// ---- Education ----
export type EducationItem = {
  id: number
  degree?: string
  org?: string
  start_year?: number | null
  start_month?: number | null
  end_year?: number | null
  end_month?: number | null
  location?: string
  summary?: string
  position?: number | null
}

export async function listEducation() {
  const { data } = await api.get<{ results: EducationItem[] }>('/api/me/profile/education/')
  return data.results || []
}
export async function createEducation(payload: Omit<EducationItem, 'id'>) {
  const { data } = await api.post<{ id: number }>('/api/me/profile/education/', payload)
  return data
}
export async function updateEducation(id: number, payload: Partial<EducationItem>) {
  const { data } = await api.patch<{ ok: boolean }>(`/api/me/profile/education/${id}/`, payload)
  return data
}
export async function deleteEducation(id: number) {
  await api.delete(`/api/me/profile/education/${id}/`)
}

// ---- Employment ----
export type EmploymentItem = {
  id: number
  title?: string
  company?: string
  start_year?: number | null
  start_month?: number | null
  end_year?: number | null
  end_month?: number | null
  location?: string
  summary?: string
  position?: number | null
}

export async function listEmployment() {
  const { data } = await api.get<{ results: EmploymentItem[] }>('/api/me/profile/employment/')
  return data.results || []
}
export async function createEmployment(payload: Omit<EmploymentItem, 'id'>) {
  const { data } = await api.post<{ id: number }>('/api/me/profile/employment/', payload)
  return data
}
export async function updateEmployment(id: number, payload: Partial<EmploymentItem>) {
  const { data } = await api.patch<{ ok: boolean }>(`/api/me/profile/employment/${id}/`, payload)
  return data
}
export async function deleteEmployment(id: number) {
  await api.delete(`/api/me/profile/employment/${id}/`)
}

// ---- Skills ----
export type SkillItem = { id?: number; skill_id: number; name?: string; proficiency?: string; years_experience?: number | null; last_used_year?: number | null; position?: number | null; verified?: boolean }

export async function getSkills() {
  const { data } = await api.get<{ results: SkillItem[] }>('/api/me/profile/skills/')
  return data.results || []
}
export async function replaceSkills(items: Array<Omit<SkillItem, 'id' | 'name'>>) {
  const { data } = await api.put<{ count: number }>(
    '/api/me/profile/skills/',
    items
  )
  return data
}

export async function seedSkills(names: string[]) {
  const { data } = await api.post<{ created: number; results: Array<{ id: number; name: string; slug: string }> }>(
    '/api/skills/seed/',
    names
  )
  return data
}

export async function requestEmailChange(email: string) {
  const { data } = await api.post<{
    ok: boolean
    pending_email: string
    verification_id: string
    expires_at: string
    email_verified: boolean
  }>('/api/me/contact/email/change/', { email })
  return data
}

export async function resendEmailChangeCode() {
  const { data } = await api.post<{ ok: boolean; pending_email: string }>('/api/me/contact/email/resend/', {})
  return data
}

export async function verifyEmailChangeCode(payload: { verification_id: string; code: string }) {
  const { data } = await api.post<{ ok: boolean; email: string; email_verified: boolean }>(
    '/api/me/contact/email/verify/',
    payload
  )
  return data
}

export async function requestPhoneOtp(phone?: string) {
  const body = phone ? { phone } : {}
  const { data } = await api.post<{ ok: boolean }>('/api/users/phone/otp/request/', body)
  return data
}

export async function verifyPhoneOtp(code: string) {
  const { data } = await api.post<{ ok: boolean; phone_verified: boolean }>('/api/users/phone/otp/verify/', { code })
  return data
}
